/** Persist request identity only. Manuscript text and execution settings stay out of storage. */
export interface PendingProofreadSubmission {
  userId: string;
  bookId: string;
  version: string;
  requestId: string;
}
const PREFIX = 'scribeshadow-proofread-submission:';
const key = (p: Pick<PendingProofreadSubmission, 'userId' | 'bookId' | 'version'>) =>
  PREFIX + JSON.stringify([p.userId, p.bookId, p.version]);

export const pendingProofreadSubmission = (p: PendingProofreadSubmission): PendingProofreadSubmission => {
  try {
    const stored = window.localStorage.getItem(key(p));
    if (stored) return { ...p, requestId: stored };
    window.localStorage.setItem(key(p), p.requestId);
  } catch { /* The in-memory request ID still protects this action. */ }
  return p;
};
export const clearPendingProofreadSubmission = (p: PendingProofreadSubmission) => {
  try {
    if (window.localStorage.getItem(key(p)) === p.requestId) window.localStorage.removeItem(key(p));
  } catch { /* Storage may be unavailable. */ }
};
export const listPendingProofreadSubmissions = (userId: string, bookId: string): PendingProofreadSubmission[] => {
  try {
    return Object.keys(window.localStorage).filter(k => k.startsWith(PREFIX)).flatMap(k => {
      const [owner, book, version] = JSON.parse(k.slice(PREFIX.length));
      const requestId = window.localStorage.getItem(k);
      return owner === userId && book === bookId && typeof version === 'string' && requestId
        ? [{ userId, bookId, version, requestId }] : [];
    });
  } catch { return []; }
};

export interface AcceptedProofreadSubmission {
  submissionId: string;
  requestId?: string;
  passId: string;
  passNumber: number;
  targetCount: number;
  lockGuid: string;
  lockKey: string;
  processingStatus?: 'pending' | 'completed' | 'failed';
  statusMessage?: string;
}

/** An uncertain paid POST is resolved by identity, never by starting another pass. */
export const submitDurableProofread = async (
  pending: PendingProofreadSubmission,
  post: (requestId: string) => Promise<AcceptedProofreadSubmission>,
  lookup: (requestId: string) => Promise<AcceptedProofreadSubmission>,
  confirming: () => void,
): Promise<AcceptedProofreadSubmission> => {
  const prior = listPendingProofreadSubmissions(pending.userId, pending.bookId).find(p => p.version === pending.version);
  const identity = pendingProofreadSubmission(pending);
  {
    // A prior tab may already have submitted this request.
    if (prior) {
      confirming();
      try {
        const accepted = await lookup(identity.requestId);
        clearPendingProofreadSubmission(identity);
        return accepted;
      } catch (error) {
        if ((error as any)?.response?.status !== 404) throw error;
      }
    }
    let accepted: AcceptedProofreadSubmission;
    try { accepted = await post(identity.requestId); }
    catch (error) {
      // Definite pre-admission rejections can be corrected with a fresh user action.
      const status = (error as any)?.response?.status;
      if (status && status >= 400 && status < 500 && status !== 408) {
        clearPendingProofreadSubmission(identity);
        throw error;
      }
      confirming();
      accepted = await lookup(identity.requestId);
    }
    clearPendingProofreadSubmission(identity);
    return accepted;
  }
};
